"use strict";

import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { toast } from "react-toastify";

import ProjectSearch from "./ProjectSearch.js";
import { COPY_CHARACTERS_TO_PROJECT } from "../ApiEndpoints.js";
import { postWithFirebaseAuth } from "../utils/firebaseApiClient.js";

// The theme paints contained buttons with a gradient `background`, which
// survives MUI's disabled state and leaves grey text on dark blue.
const importButtonSx = {
  minWidth: 200,
  fontWeight: 600,
  "&.Mui-disabled": {
    background: "#e8edf3",
    color: "#475569",
    border: "1px solid #cbd5e1",
  },
};

/**
 * Lets the user pick another project and copy its characters into `projectId`.
 * Existing characters are kept and duplicates are skipped by the backend.
 * `stacked` puts the button under the search at any screen width, for narrow
 * containers such as a side panel.
 */
const ImportCharactersPanel = ({ projectId, onImported, stacked = false }) => {
  const [sourceProject, setSourceProject] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleImport = async () => {
    // Ensure a source project has been chosen before importing.
    if (!sourceProject?.id) {
      toast.error("Please select a project to import characters from.");
      return;
    }

    try {
      setIsImporting(true);
      const response = await postWithFirebaseAuth(COPY_CHARACTERS_TO_PROJECT, {
        projectId,
        projectIds: [sourceProject.id],
      });
      const added = response.data?.added || 0;
      toast.success(
        added > 0
          ? `${added} character${added === 1 ? "" : "s"} imported from "${sourceProject.name}"`
          : `No new characters to import from "${sourceProject.name}"`
      );
      if (onImported) {
        await onImported(response.data);
      }
    } catch (error) {
      console.error("Failed to import characters:", error);
      toast.error(error.response?.data?.error || "Failed to import characters");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: stacked ? "column" : { xs: "column", md: "row" },
        alignItems: stacked ? "stretch" : { xs: "stretch", md: "center" },
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ProjectSearch
          showTitle={false}
          excludeProjectId={projectId}
          onProjectSelect={setSourceProject}
          fullWidth={stacked}
        />
      </Box>
      <Button
        variant="contained"
        color="primary"
        disabled={!sourceProject || isImporting}
        onClick={handleImport}
        sx={stacked ? { ...importButtonSx, minWidth: 0 } : importButtonSx}
      >
        {isImporting ? "Importing…" : "Import Cast"}
      </Button>
    </Box>
  );
};

export default ImportCharactersPanel;
